/**
 * Module registry.
 *
 * A module is a lazy-loaded route, not a separately deployed micro-frontend.
 * Adding one means an entry here plus a row in MODULES (packages/auth/policy.ts)
 * and a Cerbos policy file — no remoteEntry.js, no share-scope negotiation, no
 * version pinning between host and remote, no separate Docker image, Helm chart
 * or pipeline job.
 *
 * React.lazy gives the code splitting that federation was nominally providing,
 * without the coordinated-deploy requirement: each module is its own chunk,
 * fetched on first navigation.
 *
 * `id` must match the module id in policy.ts. The route itself comes from the
 * server via GET /api/me, so a module's URL is defined in exactly one place.
 */
import { lazy, type ComponentType, type LazyExoticComponent } from 'react';

export interface ModuleDefinition {
  /** Must match the module id in packages/auth/policy.ts. */
  id: string;
  /** Permission required to open the route. Enforced server-side too. */
  permission: string;
  component: LazyExoticComponent<ComponentType>;
}

export const MODULE_COMPONENTS: ModuleDefinition[] = [
  {
    id: 'dashboard',
    permission: 'dashboard:view',
    component: lazy(() => import('./dashboard/DashboardPage.js')),
  },
  {
    id: 'org-chart',
    permission: 'org-chart:view',
    component: lazy(() => import('./org-chart/OrgChartPage.js')),
  },
  {
    id: 'people',
    permission: 'people:view',
    component: lazy(() => import('./people/PeoplePage.js')),
  },
  {
    id: 'blog',
    permission: 'blog:view',
    component: lazy(() => import('./blog/BlogPage.js')),
  },
  {
    id: 'time',
    permission: 'time:view',
    component: lazy(() => import('./time/TimePage.js')),
  },
  {
    id: 'analytics',
    permission: 'analytics:view',
    component: lazy(() => import('./analytics/AnalyticsPage.js')),
  },
  {
    id: 'assets',
    permission: 'assets:view',
    component: lazy(() => import('./assets/AssetsPage.js')),
  },
  {
    id: 'validation',
    permission: 'validation:view',
    component: lazy(() => import('./validation/ValidationPage.js')),
  },
  {
    id: 'sales',
    permission: 'sales:view',
    component: lazy(() => import('./sales/SalesPage.js')),
  },
  {
    id: 'crm',
    permission: 'crm:view',
    component: lazy(() => import('./crm/CrmPage.js')),
  },
  {
    id: 'revenue',
    permission: 'revenue:view',
    component: lazy(() => import('./revenue/RevenuePage.js')),
  },
  {
    id: 'projects',
    permission: 'projects:view',
    component: lazy(() => import('./projects/ProjectsPage.js')),
  },
  {
    id: 'learning',
    permission: 'learning:view',
    component: lazy(() => import('./learning/LearningPage.js')),
  },
  {
    id: 'resourcing',
    permission: 'resourcing:view',
    component: lazy(() => import('./resourcing/ResourcingPage.js')),
  },
  {
    id: 'strategic-plan',
    permission: 'strategic-plan:view',
    component: lazy(() => import('./strategic-plan/StrategicPlanPage.js')),
  },
  {
    id: 'admin',
    permission: 'admin:admin',
    component: lazy(() => import('./admin/AccessAdminPage.js')),
  },
];

export function moduleComponent(id: string): ModuleDefinition | undefined {
  return MODULE_COMPONENTS.find((m) => m.id === id);
}
